/**
 * Packing-list editor — its OWN route, not an overlay: two nested repeaters plus
 * a PI picker is minutes of work, and an overlay has no URL to come back to.
 *
 * WEIGHTS ARE NOT MONEY. Nothing on this screen carries a price; nw/gw are plain
 * kilograms (drizzle `real`) parsed by parseKg, never parseMoneyMajor, and
 * nothing multiplies or divides by 100. Totals are summed PER CARTON — the
 * `pl_items` rows repeat their carton's weight triple, so summing rows would
 * multiply it by the number of lines. `packingTotals` mirrors the server's
 * flattenCtns() exactly so the preview cannot drift from what is stored.
 *
 * plNo and date are read-only by design: the number is allocated atomically from
 * document_counters inside the create transaction, and packingListCreateSchema
 * has no `date` at all (the service hardcodes Date.now(), matching legacy's
 * "current system date"). The UI never previews or reserves a number.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import {
  useBlocker,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Check,
  PackagePlus,
  TriangleAlert,
  X,
} from "lucide-react";
import {
  type PiItemDto,
  type PiSummaryDto,
} from "@hisense/shared";
import { PageActions, PageBody } from "@/components/ui/PageHeader";
import { usePageCrumbs } from "@/components/shell/breadcrumb";
import { Panel, PanelHeader } from "@/components/ui/Panel";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Input, FieldLabel } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { TableShell, Td, Th, Tr } from "@/components/ui/Table";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { Modal } from "@/components/ui/Modal";
import { ErrorState, LoadingState } from "@/components/ui/States";
import { CartonsEditor } from "@/components/logistics/CartonsEditor";
import { BatchCartonsPanel, type BatchLine } from "@/components/logistics/BatchCartonsPanel";
import { PiPickerTable } from "@/components/logistics/PiPickerTable";
import {
  MEASUREMENT_OPTIONS,
  blankCtn,
  ctnsFromDetail,
  draftsToInput,
  duplicateCtnNos,
  emptyCtnIndexes,
  packedByItemId,
  isUntouchedCtn,
  parseKg,
  type CtnDraft,
  type CtnItemDraft,
} from "@/components/logistics/packingDraft";
import { useAuth } from "@/app/auth";
import { useCan } from "@/app/access";
import {
  usePackingAvailability,
  useCreatePackingList,
  usePackingList,
  useProformaInvoice,
  useSettings,
  useUpdatePackingList,
} from "@/api/hooks";
import { formatDate } from "@/domain/datetime";
import { ApiClientError, getLocalizedError } from "@/lib/api";
import { SaveButton } from "@/components/ui/SaveButton";

const emptyHeader = {
  invoiceNo: "",
  attention: "",
  shippingMode: "",
  respBy: "",
  shipTo: "",
  shippingMarks: "",
};

/** Plain kilograms, 2dp — the same helper PackingListsPage already uses.
 *  Weights are NOT money: no MONEY_SCALE, no ×100, no /100. */
const fmtKg = (n: number) =>
  n.toLocaleString("en-US", {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  });

/**
 * Preview totals. Summed PER CARTON, never per row — `pl_items` repeats each
 * carton's weight triple on every one of its rows, so summing rows would
 * multiply the weight by the line count. Mirrors flattenCtns() server-side,
 * including its 3-decimal rounding, so the preview cannot drift from storage.
 */
function packingTotals(ctns: CtnDraft[]) {
  let nw = 0;
  let gw = 0;
  let items = 0;
  for (const c of ctns) {
    nw += parseKg(c.nwKgs).value ?? 0;
    gw += parseKg(c.gwKgs).value ?? 0;
    items += c.items.length;
  }
  const kg = (n: number) => Math.round(n * 1000) / 1000;
  return { ctns: ctns.length, nw: kg(nw), gw: kg(gw), items };
}

const textareaCls =
  "w-full rounded-sm border border-line-strong bg-surface px-2.5 py-2 text-sm text-ink outline-none focus:border-brand disabled:cursor-not-allowed disabled:opacity-60";

/** Một dòng PI đóng vào thùng — dùng chung cho "Thêm vào thùng" và "Tạo thùng hàng loạt". */
function ctnItemFromPi(item: PiItemDto, customerPo: string): CtnItemDraft {
  return {
    originalItemId: item.id,
    lotNumber: item.lotNumber ?? "",
    itemNo: item.productCode ?? "",
    subRef: item.subRef ?? "",
    description: item.description ?? "",
    size: item.size ?? "",
    color: item.color ?? "",
    customerPo,
    unit: item.unit ?? "",
    quantity: "",
  };
}

/**
 * Remount on id change so a cached record's form state can never bleed into the
 * next one (CLAUDE.md remount rule). The route element is stable across
 * /packing-lists/:id → /packing-lists/:otherId, so the key has to live here.
 */
export function PackingListEditorPage() {
  const { id } = useParams();
  return <PackingListEditorForm key={id ?? "new"} />;
}

function PackingListEditorForm() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { search: context } = useLocation();
  const initialized = useRef(false);
  const initialVersion=useRef<number|undefined>(undefined);
  const sourceLoaded = useRef<string | null>(null);
  const allowNavigation = useRef(false);
  const lock = useRef(false);
  const requestId = useRef(crypto.randomUUID());
  const baseline = useRef<{
    header: typeof emptyHeader;
    ctns: CtnDraft[];
  } | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [resolveConflicts, setResolveConflicts] = useState(false);
  const { user } = useAuth();
  const can = useCan();
  const isNew = !id || id === "new";

  const {
    data: existing,
    isLoading,
    error: loadError,
    refetch,
  } = usePackingList(isNew ? undefined : id);
  const settings = useSettings();
  const create = useCreatePackingList();
  const update = useUpdatePackingList(id ?? "new");

  const lists = settings.data?.businessOperations.optionLists;
  const shippingMethods = lists?.shippingMethods ?? [];
  // EMPTY in production (app_settings.business_operations = '{}'), so this must
  // degrade to free text with suggestions rather than a select with no options.
  const respByOptions = lists?.quotationPreparedBy ?? [];

  // NOTE manager holds records:create but NOT records:update — it can raise a
  // packing list and then not save an edit to it. Server-enforced either way.
  const canEdit = user
    ? can(isNew ? "records:create" : "records:update")
    : false;

  const [h, setH] = useState(emptyHeader);
  const [ctns, setCtns] = useState<CtnDraft[]>([]);
  const [piId, setPiId] = useState<string | null>(null);
  const [pickedPi, setPickedPi] = useState<PiSummaryDto | null>(null);
  /** A PI switch (or clear) that is waiting on the discard confirmation. */
  const [pendingPi, setPendingPi] = useState<{
    next: PiSummaryDto | null;
  } | null>(null);
  const [adding, setAdding] = useState<{
    itemId: string;
    ctn: string;
    qty: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Mốc lần lưu thành công gần nhất — nút Lưu đổi thành "Đã lưu" trong 2
  // giây. Không có tín hiệu nào thì người dùng bấm thêm lần nữa cho chắc.
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!existing || initialized.current) return;
    initialized.current = true;
    initialVersion.current=existing.updatedAt;
    setH({
      invoiceNo: existing.invoiceNo ?? "",
      attention: existing.attention ?? "",
      shippingMode: existing.shippingMode ?? "",
      respBy: existing.respBy ?? "",
      shipTo: existing.shipTo ?? "",
      shippingMarks: existing.shippingMarks ?? "",
    });
    setCtns(ctnsFromDetail(existing.items));
    setPiId(existing.piId);
    setDirty(false);
    baseline.current = {
      header: {
        invoiceNo: existing.invoiceNo ?? "",
        attention: existing.attention ?? "",
        shippingMode: existing.shippingMode ?? "",
        respBy: existing.respBy ?? "",
        shipTo: existing.shipTo ?? "",
        shippingMarks: existing.shippingMarks ?? "",
      },
      ctns: ctnsFromDetail(existing.items),
    };
  }, [existing]);

  const saving = create.isPending || update.isPending;
  const blocker = useBlocker(
    () => !allowNavigation.current && (dirty || lock.current),
  );
  useEffect(() => {
    const guard = (e: BeforeUnloadEvent) => {
      if (!allowNavigation.current && (dirty || lock.current)) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", guard);
    return () => window.removeEventListener("beforeunload", guard);
  }, [dirty]);
  const pi = useProformaInvoice(piId ?? undefined);
  const availability = usePackingAvailability(
    piId ?? undefined,
    isNew ? undefined : id,
  );
  useEffect(() => {
    if (
      !isNew ||
      !piId ||
      !pi.data ||
      pi.data.id !== piId ||
      sourceLoaded.current === piId
    )
      return;
    sourceLoaded.current = piId;
    setH({
      invoiceNo: pi.data.invNo ?? "",
      attention: "",
      shippingMode: pi.data.shippingMode ?? "",
      respBy: pi.data.respBy ?? "",
      shipTo: [
        pi.data.consigneeName ?? pi.data.billToName,
        pi.data.consigneeSnapshot ?? pi.data.shipTo,
      ]
        .filter(Boolean)
        .join("\n"),
      shippingMarks: pi.data.shippingMarks ?? "",
    });
  }, [isNew, piId, pi.data]);
  const piItems: PiItemDto[] = useMemo(() => pi.data?.items ?? [], [pi.data]);
  const packed = useMemo(() => packedByItemId(ctns), [ctns]);
  const totals = useMemo(() => packingTotals(ctns), [ctns]);

  const patch = (p: Partial<typeof h>) => {
    setDirty(true);
    setH((prev) => ({ ...prev, ...p }));
  };
  const patchCtns = (next: CtnDraft[]) => {
    setDirty(true);
    setCtns(next);
  };

  usePageCrumbs(
    isNew
      ? [{ label: t("packingLists.newPackingList") }]
      : existing
        ? [{ label: existing.plNo }]
        : [],
    `/packing-lists${context}`,
  );

  if (!isNew && isLoading) {
    return (
      <PageBody>
        <LoadingState />
      </PageBody>
    );
  }

  if (!isNew && loadError) {
    const status = loadError instanceof ApiClientError ? loadError.status : 0;
    return (
      <PageBody>
        <ErrorState
          message={
            status === 403
              ? t("errors.forbidden")
              : status === 404
                ? t("errors.notFound")
                : t("packingLists.errorLoad")
          }
          onRetry={
            status === 403 || status === 404 ? undefined : () => void refetch()
          }
        />
      </PageBody>
    );
  }

  if (!isNew && !existing) {
    return (
      <PageBody>
        <ErrorState message={t("errors.notFound")} />
      </PageBody>
    );
  }

  const applyPi = (next: PiSummaryDto | null) => {
    setPendingPi(null);
    setDirty(true);
    setError(null);
    setPiId(next?.id ?? null);
    sourceLoaded.current = null;
    setPickerOpen(false);
    setH(emptyHeader);
    setPickedPi(next);
    // Start with one carton so the first "add to carton" has a target.
    setCtns(next ? [blankCtn([])] : []);
    setAdding(null);
  };

  /** Switching or clearing the PI invalidates every packed line — they point at
   *  the old PI's rows, which the server's over-pack guard would reject. Confirm
   *  before throwing that work away. */
  const requestPi = (next: PiSummaryDto | null) => {
    if (
      dirty &&
      (ctns.some((c) => c.items.length) || Object.values(h).some(Boolean))
    ) {
      setPendingPi({ next });
      return;
    }
    applyPi(next);
  };

  const addToCtn = (item: PiItemDto, remaining: number) => {
    if (!adding || adding.itemId !== item.id) return;
    const ci = parseInt(adding.ctn, 10);
    const ctn = ctns[ci];
    if (!ctn) {
      setError(t("packingLists.noCtns"));
      return;
    }
    const raw = adding.qty.trim();
    if (!/^\d+$/.test(raw) || parseInt(raw, 10) <= 0) {
      setError(t("common.invalidField", { field: t("common.qty") }));
      return;
    }
    const qty = parseInt(raw, 10);
    if (qty > remaining) {
      setError(t("packingLists.errorOverQty", { max: remaining }));
      return;
    }
    setError(null);
    setAdding(null);
    patchCtns(
      ctns.map((c, j) =>
        j === ci
          ? {
              ...c,
              items: [...c.items, { ...ctnItemFromPi(item, pi.data?.customerPoNo ?? ""), quantity: String(qty) }],
            }
          : c,
      ),
    );
  };

  const save = async () => {
    if (!canEdit || lock.current || existing?.deliveryNotes?.length) return;
    setError(null);
    if (isNew && (pi.isLoading || !pi.data || sourceLoaded.current !== piId))
      return setError(t("packingLists.errorLoadPi"));
    if (isNew && !piId) return setError(t("packingLists.noPiSelected"));
    if (ctns.length === 0) return setError(t("packingLists.errorNoCtn"));

    const empties = emptyCtnIndexes(ctns);
    if (empties.length)
      return setError(
        t("packingLists.errorEmptyCtn", { count: empties.length }),
      );

    const dupes = duplicateCtnNos(ctns);
    if (dupes.length) return setError(t("errors.duplicateCtnNo"));

    const conv = draftsToInput(ctns);
    if ("error" in conv) {
      const fieldKey = {
        ctnNo: "packingLists.ctnNo",
        nwKgs: "packingLists.netWeight",
        gwKgs: "packingLists.grossWeight",
        quantity: "common.qty",
      }[conv.error.field];
      return setError(
        t("common.lineError", { line: conv.error.ctn, field: t(fieldKey) }),
      );
    }

    // Mirror of the server's over-pack rule. Advisory only — the server stays
    // the source of truth, and a stale PI read here must never block a save it
    // would have accepted, so only a definitely-known excess is refused.
    if (piItems.length) {
      const available = new Map(
        (availability.data?.items ?? []).map((it) => [it.id, it.remaining]),
      );
      for (const [itemId, qty] of packedByItemId(ctns)) {
        const max = available.get(itemId);
        if (max !== undefined && qty > max)
          return setError(t("errors.overPacked"));
      }
    }

    lock.current = true;
    try {
      if (isNew) {
        if (!piId) return setError(t("packingLists.noPiSelected"));
        const r = await create.mutateAsync({
          piId,
          requestId: requestId.current,
          // Omitted, not "": the server defaults ship-to / mode / resp-by from
          // the PI when the field is absent.
          shipTo: h.shipTo.trim() || undefined,
          shippingMarks: h.shippingMarks.trim() || undefined,
          invoiceNo: h.invoiceNo.trim() || undefined,
          attention: h.attention.trim() || undefined,
          shippingMode: h.shippingMode.trim() || undefined,
          respBy: h.respBy.trim() || undefined,
          ctns: conv.ctns,
        });
        setDirty(false);
        allowNavigation.current = true;
        navigate(`/packing-lists/${r.data.id}${context}`, { replace: true });
      } else {
        // A full-form edit: "" is a deliberate clear (opt() is optional, not
        // nullable, so an empty string writes an empty string). `ctns` present
        // means a FULL REPLACE of pl_items — which is why every draft row keeps
        // its originalItemId.
        const changedHeader = Object.fromEntries(
          Object.entries(h).filter(
            ([key, value]) =>
              value !== baseline.current?.header[key as keyof typeof h],
          ),
        );
        const linesChanged =
          resolveConflicts ||
          JSON.stringify(ctns) !== JSON.stringify(baseline.current?.ctns);
        await update.mutateAsync({
          ...changedHeader,
          ctns: linesChanged ? conv.ctns : undefined,
          expectedUpdatedAt: initialVersion.current,
          resolveCartonConflicts: resolveConflicts,
        });
        allowNavigation.current = true;
        navigate(`/packing-lists/${id}${context}`);
        setSavedAt(Date.now());
        setDirty(false);
      }
    } catch (err) {
      setError(getLocalizedError(err, t, "packingLists.errorSave"));
      if (err instanceof ApiClientError && err.code === "over_packed")
        void availability.refetch();
    } finally {
      lock.current = false;
    }
  };

  const readOnly =
    !canEdit || saving || Boolean(existing?.deliveryNotes?.length);
  const piNo = existing?.piNo ?? pickedPi?.piNo ?? null;

  return (
    <PageBody>
      <ConfirmDialog
        open={blocker.state === "blocked"}
        title={t("common.unsavedTitle")}
        description={t("common.unsavedDescription")}
        confirmLabel={t("common.discardChanges")}
        cancelLabel={t("common.keepEditing")}
        busy={saving}
        onCancel={() => blocker.reset?.()}
        onConfirm={() => blocker.proceed?.()}
      />
      <h1 className="text-xl font-semibold">
        {t(isNew ? "packingLists.newPackingList" : "packingWorkspace.edit")}
        {existing && <span className="ml-2 font-mono">{existing.plNo}</span>}
      </h1>
      {existing?.deliveryNotes?.length ? (
        <p className="mt-3 text-sm text-warn">
          {t("packingWorkspace.packing_delivery_locked")}
        </p>
      ) : null}
      {existing?.cartonConflicts && (
        <label className="my-3 flex gap-2 text-xs text-warn">
          <input
            type="checkbox"
            checked={resolveConflicts}
            onChange={(e) => {
              setResolveConflicts(e.target.checked);
              setDirty(true);
            }}
          />
          {t("packingWorkspace.resolveConflicts")}
        </label>
      )}
      <PageActions>
        {canEdit && !existing?.deliveryNotes?.length && (
          <SaveButton
            size="sm"
            onClick={() => void save()}
            saving={saving}
            savedAt={savedAt}
          />
        )}
      </PageActions>

      {readOnly && (
        <p className="mt-3 rounded-sm bg-sunken px-3 py-2 text-xs text-ink-soft">
          {t("errors.readOnly")}
        </p>
      )}

      <Panel className="mt-4 flex items-center justify-between gap-3 p-4">
        <div>
          <FieldLabel>{t("packingLists.selectPi")}</FieldLabel>
          <p className="font-mono text-sm text-brand">
            {piNo ?? t("packingLists.noPiSelected")}
          </p>
        </div>
        {isNew && !readOnly && (
          <Button variant="secondary" onClick={() => setPickerOpen(true)}>
            {t("packingWorkspace.choosePi")}
          </Button>
        )}
      </Panel>
      {pickerOpen && (
        <Modal
          className="max-w-5xl"
          title={t("packingLists.selectPi")}
          closeLabel={t("common.close")}
          onClose={() => setPickerOpen(false)}
        >
          <div className="p-4">
            <PiPickerTable
              value={piId}
              onSelect={requestPi}
              onClear={() => requestPi(null)}
              disabled={saving}
            />
          </div>
        </Modal>
      )}
      {/* ── Header ── */}
      <Panel className="mt-3 p-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <FieldLabel>{t("common.date")}</FieldLabel>
            <p className="min-h-9 rounded-sm border border-line bg-sunken px-2.5 py-2 font-mono tabular text-sm text-ink-soft">
              {formatDate(existing?.date??Date.now())}
            </p>
          </div>
          <div>
            <FieldLabel htmlFor="pl-invoice-no">
              {t("packingLists.invoiceNo")}
            </FieldLabel>
            <Input
              id="pl-invoice-no"
              value={h.invoiceNo}
              disabled={readOnly}
              onChange={(e) => patch({ invoiceNo: e.target.value })}
            />
          </div>
          <div>
            <FieldLabel htmlFor="pl-attention">
              {t("packingLists.attention")}
            </FieldLabel>
            <Input
              id="pl-attention"
              value={h.attention}
              disabled={readOnly}
              onChange={(e) => patch({ attention: e.target.value })}
            />
          </div>
          <div>
            <FieldLabel htmlFor="pl-shipping-mode">
              {t("packingLists.shippingMode")}
            </FieldLabel>
            <Input
              id="pl-shipping-mode"
              list="pl-shipping-modes"
              value={h.shippingMode}
              disabled={readOnly}
              onChange={(e) => patch({ shippingMode: e.target.value })}
            />
            <datalist id="pl-shipping-modes">
              {shippingMethods.map((m) => (
                <option key={m} value={m} />
              ))}
            </datalist>
          </div>
          <div>
            {/* optionLists.quotationPreparedBy is empty in production — free
                text with suggestions, never a select with nothing in it. */}
            <FieldLabel htmlFor="pl-resp-by">{t("pi.respBy")}</FieldLabel>
            <Input
              id="pl-resp-by"
              list="pl-resp-by-options"
              value={h.respBy}
              disabled={readOnly}
              onChange={(e) => patch({ respBy: e.target.value })}
            />
            <datalist id="pl-resp-by-options">
              {respByOptions.map((p) => (
                <option key={p} value={p} />
              ))}
            </datalist>
          </div>
          <div className="sm:col-span-2">
            <FieldLabel htmlFor="pl-ship-to">{t("common.shipTo")}</FieldLabel>
            <textarea
              id="pl-ship-to"
              rows={3}
              className={textareaCls}
              value={h.shipTo}
              disabled={readOnly}
              onChange={(e) => patch({ shipTo: e.target.value })}
            />
          </div>
          <div className="sm:col-span-2">
            <FieldLabel htmlFor="pl-shipping-marks">
              {t("packingLists.shippingMarks")}
            </FieldLabel>
            <textarea
              id="pl-shipping-marks"
              rows={3}
              className={textareaCls}
              value={h.shippingMarks}
              disabled={readOnly}
              onChange={(e) => patch({ shippingMarks: e.target.value })}
            />
          </div>
        </div>
      </Panel>

      {/* ── Items still available to pack ── derived, never stored. */}
      <Panel className="mt-3">
        <PanelHeader title={t("packingLists.availableItems")} />
        <div className="p-3">
          {!piId ? (
            <p className="py-6 text-center text-sm text-ink-soft">
              {t("packingLists.noPiSelected")}
            </p>
          ) : pi.isLoading ? (
            <LoadingState />
          ) : pi.error ? (
            <ErrorState
              message={t("packingLists.errorLoadPi")}
              onRetry={() => void pi.refetch()}
            />
          ) : piItems.length === 0 ? (
            <p className="py-6 text-center text-sm text-ink-soft">
              {t("pi.linesCount", { count: 0 })}
            </p>
          ) : (
            <TableShell>
              <thead>
                <tr>
                  <Th pin>{t("packingLists.itemNo")}</Th>
                  <Th>{t("common.subRef")}</Th>
                  <Th>{t("common.description")}</Th>
                  <Th>{t("common.size")}</Th>
                  <Th>{t("common.color")}</Th>
                  <Th align="right">{t("packingLists.originalQty")}</Th>
                  <Th align="right">
                    {t("packingWorkspace.allocatedElsewhere")}
                  </Th>
                  <Th align="right">{t("packingLists.packedQty")}</Th>
                  <Th align="right">{t("packingLists.remainingQty")}</Th>
                  <Th>{t("common.unit")}</Th>
                  <Th>{t("common.status")}</Th>
                  {!readOnly && (
                    <Th align="right">{t("packingLists.addToCtn")}</Th>
                  )}
                </tr>
              </thead>
              <tbody>
                {piItems.map((it) => {
                  const done = packed.get(it.id) ?? 0;
                  const elsewhere =
                    availability.data?.items.find((r) => r.id === it.id)
                      ?.allocated ?? 0;
                  const remaining = Math.max(0, it.quantity - elsewhere - done);
                  // Captured, not `adding?.itemId === it.id`: a boolean alias
                  // would not narrow `adding` inside the ternary below.
                  const addRow =
                    adding && adding.itemId === it.id ? adding : null;
                  return (
                    <Tr key={it.id}>
                      <Td pin className="font-mono tabular text-xs text-brand">
                        {it.productCode ?? "—"}
                      </Td>
                      <Td className="font-mono tabular text-xs text-ink-soft">
                        {it.subRef ?? "—"}
                      </Td>
                      <Td className="text-ink">{it.description ?? "—"}</Td>
                      <Td className="text-xs text-ink-soft">
                        {it.size ?? "—"}
                      </Td>
                      <Td className="text-xs text-ink-soft">
                        {it.color ?? "—"}
                      </Td>
                      <Td
                        align="right"
                        className="font-mono tabular text-ink-soft"
                      >
                        {it.quantity}
                      </Td>
                      <Td
                        align="right"
                        className="font-mono tabular text-ink-soft"
                      >
                        {elsewhere}
                      </Td>
                      <Td
                        align="right"
                        className="font-mono tabular text-ink-soft"
                      >
                        {done}
                      </Td>
                      <Td
                        align="right"
                        className="font-mono tabular font-medium text-ink"
                      >
                        {remaining}
                      </Td>
                      <Td className="text-xs text-ink-soft">
                        {it.unit ?? "—"}
                      </Td>
                      <Td>
                        <Badge tone={remaining > 0 ? "ok" : "neutral"}>
                          {remaining > 0
                            ? t("packingLists.available")
                            : t("packingLists.depleted")}
                        </Badge>
                      </Td>
                      {!readOnly && (
                        <Td align="right">
                          {addRow ? (
                            <span className="flex items-center justify-end gap-1.5">
                              <Select
                                containerClassName="h-8 w-28"
                                className="text-xs"
                                aria-label={t("packingLists.selectCtn")}
                                value={addRow.ctn}
                                onChange={(e) =>
                                  setAdding({ ...addRow, ctn: e.target.value })
                                }
                              >
                                {ctns.map((c, j) => (
                                  <option key={j} value={String(j)}>
                                    {c.ctnNo}
                                  </option>
                                ))}
                              </Select>
                              <input
                                className="h-8 w-20 rounded-xs border border-line bg-surface px-1.5 text-right font-mono tabular text-xs text-ink outline-none focus:border-brand"
                                inputMode="numeric"
                                aria-label={t("common.qty")}
                                value={addRow.qty}
                                onChange={(e) =>
                                  setAdding({ ...addRow, qty: e.target.value })
                                }
                              />
                              <Button
                                size="icon-sm"
                                aria-label={t("common.add")}
                                onClick={() => addToCtn(it, remaining)}
                              >
                                <Check
                                  className="size-3.5"
                                  aria-hidden="true"
                                />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon-sm"
                                aria-label={t("common.cancel")}
                                onClick={() => setAdding(null)}
                              >
                                <X className="size-3.5" aria-hidden="true" />
                              </Button>
                            </span>
                          ) : (
                            <Button
                              variant="secondary"
                              size="sm"
                              disabled={
                                remaining <= 0 ||
                                ctns.length === 0 ||
                                availability.isFetching ||
                                Boolean(availability.error)
                              }
                              title={
                                ctns.length === 0
                                  ? t("packingLists.noCtns")
                                  : undefined
                              }
                              onClick={() => {
                                setError(null);
                                setAdding({
                                  itemId: it.id,
                                  ctn: "0",
                                  qty: String(remaining),
                                });
                              }}
                            >
                              <PackagePlus
                                className="size-3.5"
                                aria-hidden="true"
                              />{" "}
                              {t("packingLists.addToCtn")}
                            </Button>
                          )}
                        </Td>
                      )}
                    </Tr>
                  );
                })}
              </tbody>
            </TableShell>
          )}
        </div>
      </Panel>

      {availability.error && (
        <ErrorState
          message={t("packingWorkspace.availabilityError")}
          onRetry={() => void availability.refetch()}
        />
      )}
      {/* ── Cartons ── */}
      <div className="mt-3">
        <CartonsEditor
          ctns={ctns}
          onChange={patchCtns}
          measurementOptions={MEASUREMENT_OPTIONS}
          readOnly={readOnly}
        />
      </div>
      {/* Tạo thùng hàng loạt theo nhóm (yêu cầu v1.8 mục 7.1). Số còn lại tính như bảng "Hàng chờ đóng" ở trên. */}
      {!readOnly && piItems.length > 0 && (
        <div className="mt-3">
          <BatchCartonsPanel
            ctns={ctns}
            measurementOptions={MEASUREMENT_OPTIONS}
            lines={piItems.map((it): BatchLine => {
              const elsewhere = availability.data?.items.find((r) => r.id === it.id)?.allocated ?? 0;
              return {
                id: it.id,
                code: it.productCode ?? "",
                description: it.description ?? "",
                unit: it.unit ?? "",
                remaining: Math.max(0, it.quantity - elsewhere - (packed.get(it.id) ?? 0)),
                item: ctnItemFromPi(it, pi.data?.customerPoNo ?? ""),
              };
            })}
            // Thùng mồi trắng trang mở sẵn bị thay, không để lại một thùng rỗng chặn lưu.
            onCreate={(created) => patchCtns([...ctns.filter((c) => !isUntouchedCtn(c)), ...created])}
          />
        </div>
      )}

      {/* ── Totals ── summed PER CARTON, mirroring flattenCtns(). */}
      <Panel className="mt-3 grid grid-cols-2 gap-3 p-4 sm:grid-cols-4">
        <div>
          <FieldLabel>{t("common.cartons")}</FieldLabel>
          <p className="font-mono tabular text-lg font-semibold text-ink">
            {totals.ctns}
          </p>
        </div>
        <div>
          <FieldLabel>{t("packingLists.netWeight")}</FieldLabel>
          <p className="font-mono tabular text-lg font-semibold text-ink">
            {ctns.some((c) => !c.nwKgs.trim()) ? "—" : fmtKg(totals.nw)}
          </p>
        </div>
        <div>
          <FieldLabel>{t("packingLists.grossWeight")}</FieldLabel>
          <p className="font-mono tabular text-lg font-semibold text-ink">
            {ctns.some((c) => !c.gwKgs.trim()) ? "—" : fmtKg(totals.gw)}
          </p>
        </div>
        <div>
          <FieldLabel>{t("common.items")}</FieldLabel>
          <p className="font-mono tabular text-lg font-semibold text-ink">
            {totals.items}
          </p>
        </div>
      </Panel>

      {error && (
        <p
          role="alert"
          className="mt-3 rounded-sm bg-danger-tint px-3 py-2 text-xs text-danger"
        >
          {error}
        </p>
      )}

      <div className="sticky bottom-0 mt-3 flex justify-end gap-2 border-t border-line bg-surface p-3">
        <Button
          variant="secondary"
          disabled={saving}
          onClick={() =>
            navigate(
              isNew
                ? `/packing-lists${context}`
                : `/packing-lists/${id}${context}`,
            )
          }
        >
          {t("common.cancel")}
        </Button>
        <Button
          disabled={readOnly || (isNew && !piId)}
          onClick={() => void save()}
        >
          {t(saving ? "common.saving" : "common.save")}
        </Button>
      </div>
      {/* Switching the PI throws away every packed carton — confirm it. */}
      {pendingPi && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-ink/35 p-4 backdrop-blur-[2px]"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setPendingPi(null);
          }}
        >
          <div
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="pl-pi-confirm-title"
            aria-describedby="pl-pi-confirm-desc"
            className="w-full max-w-md rounded-lg border border-line bg-surface shadow-[var(--shadow-pop)]"
          >
            <div className="flex gap-3 px-5 py-5">
              <span className="grid size-9 shrink-0 place-items-center rounded-sm bg-warn-tint text-warn">
                <TriangleAlert className="size-5" aria-hidden="true" />
              </span>
              <div className="min-w-0">
                <h3
                  id="pl-pi-confirm-title"
                  className="text-lg font-semibold text-ink"
                >
                  {t("common.unsavedTitle")}
                </h3>
                <p
                  id="pl-pi-confirm-desc"
                  className="mt-1.5 text-sm leading-relaxed text-ink-soft"
                >
                  {t("common.unsavedDescription")}
                </p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-line bg-paper px-5 py-3.5">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setPendingPi(null)}
              >
                {t("common.keepEditing")}
              </Button>
              <Button
                variant="danger"
                size="sm"
                onClick={() => applyPi(pendingPi.next)}
              >
                {t("common.discardChanges")}
              </Button>
            </div>
          </div>
        </div>
      )}
    </PageBody>
  );
}
